"use client";

import { useLanguage } from "@/components/LanguageProvider";
import type { SearchResult } from "@/lib/api";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { FormEvent, useEffect, useState } from "react";

function typeLabel(type: string, t: (k: string) => string): string {
  switch (type) {
    case "blog":
      return t("search.type.blog");
    case "product":
      return t("search.type.product");
    case "service":
      return t("search.type.service");
    case "page":
      return t("search.type.page");
    default:
      return type;
  }
}

type Props = {
  initialQuery: string;
  initialResults: SearchResult[];
};

export function SearchContent({ initialQuery, initialResults }: Props) {
  const { t, dir } = useLanguage();
  const router = useRouter();
  const [query, setQuery] = useState(initialQuery);
  const hasQuery = initialQuery.length >= 2;

  useEffect(() => {
    setQuery(initialQuery);
  }, [initialQuery]);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const q = query.trim();
    router.push(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-16 md:py-24" dir={dir}>
      <h1 className="text-3xl font-extrabold text-[var(--primary)]">
        {t("search.title")}
      </h1>

      <form onSubmit={onSubmit} className="mt-8 flex gap-2" role="search">
        <label htmlFor="site-search" className="sr-only">
          {t("search.placeholder")}
        </label>
        <input
          id="site-search"
          name="q"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("search.placeholder")}
          minLength={2}
          className="min-h-11 flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-slate-900 outline-none ring-[var(--accent)] focus:ring-2"
        />
        <button
          type="submit"
          className="inline-flex min-h-11 items-center justify-center rounded-xl bg-[var(--accent)] px-6 text-sm font-bold text-[var(--accent-foreground)]"
        >
          {t("search.submit")}
        </button>
      </form>

      {query.trim().length > 0 && query.trim().length < 2 && (
        <p className="mt-4 text-sm text-slate-600">{t("search.hint")}</p>
      )}

      {!hasQuery && (
        <p className="mt-8 text-slate-600">{t("search.noQuery")}</p>
      )}

      {hasQuery && initialResults.length === 0 && (
        <p className="mt-8 text-slate-600">{t("search.none")}</p>
      )}

      {initialResults.length > 0 && (
        <div className="mt-10 space-y-4">
          <p className="text-sm text-slate-600">
            {t("search.resultsFor")}{" "}
            <strong className="text-slate-900">&ldquo;{initialQuery}&rdquo;</strong>
          </p>
          <ul className="space-y-3">
            {initialResults.map((hit) => (
              <li key={`${hit.type}-${hit.url}`}>
                <Link
                  href={hit.url}
                  className="block rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:border-[var(--accent)] hover:shadow-md"
                >
                  <span className="text-xs font-semibold uppercase tracking-wider text-[var(--accent)]">
                    {typeLabel(hit.type, t)}
                  </span>
                  <span className="mt-1 block text-lg font-semibold text-[var(--primary)]">
                    {hit.title}
                  </span>
                  {hit.excerpt ? (
                    <span className="mt-2 block text-sm text-slate-600 line-clamp-2">
                      {hit.excerpt}
                    </span>
                  ) : null}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
